// @ts-nocheck
// ─────────────────────────────────────────────────────────────
// TherapistRegister.tsx — AnımsamA Platform
// Terapist kayıt formu · Supabase Auth + profiles tablosu
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { supabase } from "./supabase";

const T = {
  primary: "#3A6EA5", primaryL: "#EBF2FA",
  green: "#7FB069",   greenL: "#EFF7EC",   greenD: "#5A8A48",
  danger: "#DC2626",  dangerL: "#FEE2E2",
  bg: "#EBEBEB",      surface: "#FFFFFF",  surfaceAlt: "#F5F5F5",
  border: "#DEDEDE",  borderD: "#C8C8C8",
  text: "#1A1A2E",    textSub: "#3A3A4A",
  muted: "#6B6B7A",   mutedL: "#9A9AAA",
};

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export default function TherapistRegister({ onBack, onSuccess }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    title: "",          // Doç. Dr. / Uzm. Psikolog vb.
    institution: "",
    department: "",
    email: "",
    phone: "",
    license: "",        // Lisans no
    specialization: "", // Uzmanlık alanı
    password: "",
    passwordConfirm: "",
  });

  const set = (key: string) => (e: any) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // ── Kayıt işlemi ─────────────────────────────────────────
  const handleSubmit = async () => {
    // Validasyon
    if (!form.fullName.trim() || !form.email.trim() || !form.password) {
      setErr("Ad Soyad, E-posta ve Şifre zorunludur."); return;
    }
    if (form.password.length < 6) {
      setErr("Şifre en az 6 karakter olmalıdır."); return;
    }
    if (form.password !== form.passwordConfirm) {
      setErr("Şifreler eşleşmiyor."); return;
    }
    if (!supabase) { setErr("Sistem bağlantısı kurulamadı."); return; }

    setLoading(true); setErr("");
    try {
      // 1) Auth kaydı
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        options: {
          data: {
            full_name: form.fullName.trim(),
            role: "therapist",
          },
        },
      });

      if (authErr) {
        if (authErr.message.includes("already registered") || authErr.message.includes("already been registered")) {
          throw new Error("Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin.");
        }
        throw new Error(authErr.message);
      }

      const userId = authData?.user?.id;
      if (!userId) throw new Error("Kullanıcı oluşturulamadı.");

      // 2) Profiles tablosuna kayıt
      const { error: profileErr } = await supabase.from("profiles").upsert({
        id: userId,
        name: form.fullName.trim(),
        title: form.title.trim(),
        institution: form.institution.trim(),
        department: form.department.trim(),
        phone: form.phone.trim(),
        license: form.license.trim(),
        specialization: form.specialization.trim(),
        role: "therapist",
        email: form.email.trim().toLowerCase(),
      });

      if (profileErr) {
        console.error("Profil kaydı hatası:", profileErr);
        // Auth kaydı başarılıysa profil hatası kritik değil
      }

      setSuccess(true);
      setTimeout(() => onSuccess(), 3000);
    } catch (e: any) {
      setErr(e.message || "Kayıt başarısız. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const inp = (label: string, key: string, opts: any = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: T.mutedL, display: "block", marginBottom: 5, letterSpacing: 1, textTransform: "uppercase" }}>
        {label}{opts.required && <span style={{ color: T.danger }}> *</span>}
      </label>
      <input
        type={opts.type || "text"}
        value={(form as any)[key]}
        onChange={set(key)}
        placeholder={opts.placeholder || ""}
        autoComplete={opts.autoComplete || "off"}
        style={{ width: "100%", background: T.surface, border: `1.5px solid ${T.borderD}`, borderRadius: 10, padding: "11px 13px", color: T.text, fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" as const }}
      />
    </div>
  );

  if (success) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18, padding: "40px 48px", textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.green, marginBottom: 10 }}>Kayıt Başarılı!</div>
        <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.8, marginBottom: 8 }}>
          <strong>{form.email}</strong> adresine onay e-postası gönderildi.<br />
          E-postanızı onayladıktan sonra giriş yapabilirsiniz.
        </p>
        <p style={{ fontSize: 12, color: T.mutedL }}>Giriş sayfasına yönlendiriliyorsunuz...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(145deg,#E2E2E6,#EBEBEB)", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px", fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      <div style={{ background: T.surface, borderRadius: 18, boxShadow: "0 8px 40px rgba(30,45,61,.12)", width: "100%", maxWidth: 520, overflow: "hidden" }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg,${T.greenD},${T.green})`, padding: "24px 32px" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", letterSpacing: -.5, marginBottom: 4 }}>
            Anımsam<span style={{ color: "#D4F0C0" }}>A</span>
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,.75)" }}>Terapist / Uzman Kaydı</div>
        </div>

        {/* İlerleme */}
        <div style={{ display: "flex", padding: "16px 32px 0", gap: 8 }}>
          {[1, 2].map(s => (
            <div key={s} style={{ flex: 1, height: 3, borderRadius: 4, background: s <= step ? T.green : T.border, transition: "background .3s" }} />
          ))}
        </div>
        <div style={{ padding: "8px 32px 0", fontSize: 11, color: T.mutedL }}>
          Adım {step}/2 — {step === 1 ? "Kişisel Bilgiler" : "Hesap Bilgileri"}
        </div>

        {/* Form */}
        <div style={{ padding: "20px 32px 28px" }}>
          {step === 1 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
                <div style={{ gridColumn: "1 / -1" }}>{inp("Ad Soyad", "fullName", { required: true, placeholder: "Dr. Ayşe Kaya" })}</div>
                {inp("Ünvan", "title", { placeholder: "Uzm. Psikolog / Doç. Dr." })}
                {inp("Kurum", "institution", { placeholder: "Üniversite / Klinik" })}
                {inp("Bölüm", "department", { placeholder: "Sosyal Hizmet / Psikoloji" })}
                {inp("Telefon", "phone", { placeholder: "0555 000 00 00", type: "tel" })}
                <div style={{ gridColumn: "1 / -1" }}>{inp("Lisans No", "license", { placeholder: "PSK-2024-XXXXX (opsiyonel)" })}</div>
                <div style={{ gridColumn: "1 / -1" }}>{inp("Uzmanlık Alanı", "specialization", { placeholder: "Anımsama Terapisi, Geropsikiyatri..." })}</div>
              </div>
              <button
                onClick={() => {
                  if (!form.fullName.trim()) { setErr("Ad Soyad zorunludur."); return; }
                  setErr(""); setStep(2);
                }}
                style={{ width: "100%", background: T.green, color: "#fff", border: "none", borderRadius: 12, padding: "13px 0", fontWeight: 600, fontSize: 14, cursor: "pointer", marginTop: 6, fontFamily: "inherit" }}>
                Devam Et →
              </button>
            </>
          )}

          {step === 2 && (
            <>
              {inp("Kurumsal E-Posta", "email", { required: true, type: "email", placeholder: "ad.soyad@kurum.edu.tr", autoComplete: "email" })}
              {inp("Şifre", "password", { required: true, type: "password", placeholder: "En az 6 karakter", autoComplete: "new-password" })}
              {inp("Şifre Tekrar", "passwordConfirm", { required: true, type: "password", placeholder: "Şifrenizi tekrar girin", autoComplete: "new-password" })}

              {err && (
                <div style={{ background: T.dangerL, border: `1px solid ${T.danger}30`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: T.danger, marginBottom: 14 }}>
                  {err}
                </div>
              )}

              <div style={{ background: T.greenL, border: `1px solid ${T.green}30`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: T.muted, marginBottom: 16, lineHeight: 1.7 }}>
                <strong style={{ color: T.greenD }}>Güvenlik:</strong> Tüm veriler KVKK ve GDPR kapsamında şifrelenir. E-posta adresinize onay bağlantısı gönderilecektir.
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => { setErr(""); setStep(1); }}
                  style={{ flex: 1, background: T.surfaceAlt, color: T.muted, border: `1.5px solid ${T.border}`, borderRadius: 12, padding: "12px 0", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  ← Geri
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 2, background: loading ? T.border : T.green, color: "#fff", border: "none", borderRadius: 12, padding: "12px 0", fontWeight: 600, fontSize: 14, cursor: loading ? "default" : "pointer", fontFamily: "inherit", transition: "all .2s" }}>
                  {loading ? "Kaydediliyor..." : "Kayıt Ol"}
                </button>
              </div>
            </>
          )}

          <div style={{ marginTop: 20, textAlign: "center", paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 13, color: T.muted }}>Zaten hesabınız var mı? </span>
            <button onClick={onBack} style={{ fontSize: 13, color: T.green, fontWeight: 700, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
